/**
 * FilterBar
 *
 * A compact, reusable filter bar rendered at the top of all list pages
 * (Matches, Tournaments, Drop-ins, Leagues, Teams).
 *
 * Shows:
 *  - Sport pills — one per user's registered sport, toggleable
 *  - Location badge with "Use My Location" (GPS) or location search
 *  - Auto-expansion notice when radius was increased
 *
 * Radius rings are 5 / 20 / 50 / 100 km (RADIUS_RINGS, 50 km by default).
 * Users choose a location by GPS or by searching — no manual lat/lng entry.
 * There is no "no location" mode: lists are always local to someone.
 */

import React, { useState, useRef } from 'react';
import { useUserFilters, RADIUS_RINGS } from '../../contexts/UserFiltersContext';
import './FilterBar.css';

const MIN_SEARCH_CHARS = 3;

/**
 * @param {object}  props
 * @param {boolean} [props.showLocation=true]  - Show the location section
 * @param {boolean} [props.showSports=true]    - Show the sport pills section
 * @param {boolean} [props.inline=false]       - Remove card background/border (embed inside another card)
 * @param {string}  [props.className]          - Extra CSS class on the wrapper
 */
const FilterBar = ({ showLocation = true, showSports = true, inline = false, className = '' }) => {
  const {
    userSports,
    selectedSportIds,
    location,
    radius,
    expandedRadius,
    radiusExpansionMsg,
    setRadius,
    toggleSport,
    selectAllSports,
    requestGpsLocation,
    setManualLocation,
    needsLocation,
  } = useUserFilters();

  const [gpsLoading, setGpsLoading]               = useState(false);
  const [gpsError, setGpsError]                   = useState('');
  const [showSearch, setShowSearch]               = useState(false);
  const [locationSearch, setLocationSearch]       = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationLoading, setLocationLoading]     = useState(false);

  const debounceRef = useRef(null);
  const abortRef    = useRef(null);

  // Don't render if we have nothing to show
  if (!userSports.length && !location && !showLocation) return null;

  // ── Location search (Geoapify autocomplete) ────────────────────────────────

  const fetchLocationSuggestions = (q) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
    if (!apiKey || q.trim().length < MIN_SEARCH_CHARS) {
      setLocationSuggestions([]);
      setLocationLoading(false);
      return;
    }

    setLocationLoading(true);
    // type=city restricts results to city-level only (no street addresses).
    // This ensures the lat/lng is a city centroid, giving a meaningful radius search.
    const url =
      `https://api.geoapify.com/v1/geocode/autocomplete` +
      `?text=${encodeURIComponent(q)}&limit=5&type=city&apiKey=${apiKey}`;

    fetch(url, { signal: abortRef.current.signal })
      .then((r) => r.json())
      .then((data) => {
        setLocationLoading(false);
        setLocationSuggestions(data?.features ?? []);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setLocationLoading(false);
      });
  };

  const handleLocationInput = (e) => {
    const q = e.target.value;
    setLocationSearch(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchLocationSuggestions(q), 400);
  };

  const handleSelectSuggestion = (feature) => {
    const p = feature.properties;
    // Prefer city name over address line for cleaner label
    const label =
      p.city || p.county || p.name || p.address_line1 || p.formatted?.split(',')[0] || locationSearch;
    setManualLocation(p.lat, p.lon, label);
    setShowSearch(false);
    setLocationSearch('');
    setLocationSuggestions([]);
    setGpsError('');
  };

  const closeSearch = () => {
    if (abortRef.current) abortRef.current.abort();
    clearTimeout(debounceRef.current);
    setShowSearch(false);
    setLocationSearch('');
    setLocationSuggestions([]);
  };

  // ── GPS handler ────────────────────────────────────────────────────────────

  const handleGps = async () => {
    setGpsError('');
    setGpsLoading(true);
    try {
      await requestGpsLocation();
      closeSearch();
    } catch {
      setGpsError('Location access denied. Please search for a location instead.');
    } finally {
      setGpsLoading(false);
    }
  };

  const activeRadius = expandedRadius || radius;
  const allSelected  = userSports.length > 0 &&
    userSports.every((s) => selectedSportIds.includes(s.sport_id || s.id));

  return (
    <div className={`filter-bar ${inline ? 'filter-bar--inline' : ''} ${className}`} data-testid="filter-bar">

      {/* ── Sport pills ───────────────────────────────────────────────── */}
      {showSports && userSports.length > 0 && (
        <div className="filter-bar__section">
          <span className="filter-bar__label">Sports</span>
          <div className="filter-bar__pills">
            <button
              className={`filter-bar__pill filter-bar__pill--all ${allSelected ? 'active' : ''}`}
              onClick={selectAllSports}
              data-testid="pill-all"
            >
              All
            </button>
            {userSports.map((sport) => {
              const id      = sport.sport_id || sport.id;
              const active  = selectedSportIds.includes(id);
              return (
                <button
                  key={id}
                  className={`filter-bar__pill ${active ? 'active' : ''}`}
                  onClick={() => toggleSport(id)}
                  data-testid={`pill-sport-${id}`}
                >
                  {sport.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Location ─────────────────────────────────────────────────── */}
      {showLocation && (
        <div className="filter-bar__section filter-bar__location-section">
          <span className="filter-bar__label">📍 Location</span>

          <div className="filter-bar__location-row">
            {location ? (
              <>
                <span
                  className={`filter-bar__location-badge filter-bar__location-badge--${location.source}`}
                  title={`Source: ${location.source}`}
                >
                  {location.source === 'gps' && '🟢 '}
                  {location.source === 'profile' && '🏠 '}
                  {location.source === 'manual' && '📌 '}
                  {location.label}
                </span>

                <button
                  className="filter-bar__btn filter-bar__btn--secondary"
                  onClick={() => setShowSearch((v) => !v)}
                  data-testid="btn-change-location"
                >
                  Change
                </button>
              </>
            ) : (
              <button
                className="filter-bar__btn filter-bar__btn--primary"
                onClick={handleGps}
                disabled={gpsLoading}
                data-testid="btn-use-location"
              >
                {gpsLoading ? '…' : '📍 Use My Location'}
              </button>
            )}

            {!location && (
              <button
                className="filter-bar__btn filter-bar__btn--ghost"
                onClick={() => setShowSearch((v) => !v)}
                data-testid="btn-enter-manual"
              >
                Search location
              </button>
            )}
          </div>

          {needsLocation && !gpsError && (
            <p className="filter-bar__error" data-testid="location-required">
              Set your location to see games, teams and events near you.
            </p>
          )}

          {gpsError && (
            <p className="filter-bar__error" data-testid="gps-error">{gpsError}</p>
          )}

          {/* Location search (replaces manual lat/lng entry) */}
          {showSearch && (
            <div
              className="filter-bar__manual-form"
              data-testid="manual-location-form"
            >
              <div className="filter-bar__location-search-wrap">
                <input
                  className="filter-bar__input"
                  type="text"
                  placeholder="Search city (e.g. Toronto, Oshawa)"
                  value={locationSearch}
                  onChange={handleLocationInput}
                  autoComplete="off"
                  data-testid="input-location-search"
                />
                {locationLoading && (
                  <span className="filter-bar__search-spinner">…</span>
                )}
                {locationSuggestions.length > 0 && (
                  <ul
                    className="filter-bar__suggestions"
                    data-testid="location-suggestions"
                  >
                    {locationSuggestions.map((feature, i) => (
                      <li
                        key={feature.properties?.place_id || i}
                        className="filter-bar__suggestion-item"
                        onClick={() => handleSelectSuggestion(feature)}
                        data-testid={`location-suggestion-${i}`}
                      >
                        {feature.properties?.formatted || feature.properties?.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                className="filter-bar__btn filter-bar__btn--ghost"
                onClick={closeSearch}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Radius expansion notice */}
          {radiusExpansionMsg && (
            <p
              className="filter-bar__expansion-msg"
              data-testid="radius-expansion-msg"
            >
              ℹ️ {radiusExpansionMsg}
            </p>
          )}
        </div>
      )}

      {/* Active radius indicator + radius selector */}
      {showLocation && location && activeRadius > 0 && (
        <div className="filter-bar__active-summary" data-testid="active-summary">
          Showing within{' '}
          {RADIUS_RINGS.map((km) => (
            <button
              key={km}
              className={`filter-bar__radius-pill ${
                activeRadius === km ? 'filter-bar__radius-pill--active' : ''
              }`}
              onClick={() => setRadius(km)}
              data-testid={`radius-pill-${km}`}
            >
              {km} km
            </button>
          ))}
          {' '}of <strong>{location.label}</strong>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
